import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { Divider, message, Modal } from 'antd';
import { stringify } from 'qs';
import Avatar from 'react-avatar';
import { useSelector } from 'react-redux';
import { useJobsForm } from 'views/jobs/hooks/useJobsForm';

import { listMemberships } from '@lib/api/endpoints/invites';
import { UsersCommandApi, UsersQueryApi } from '@lib/api/endpoints/users';
import { RootState } from '@lib/redux.lib/store';
import { UK_COUNTRY } from '@lib/utils/constants';
import {
  WARDROBE_LABELS_BY_GENDER,
  WARDROBE_LABELS_BY_GENDER_UK,
  WardrobeMapping,
  WardrobeMappingUK,
} from '@lib/utils/constants/profileConstants';
import theme from '@styles/theme';
import {
  AvailableButton,
  AvailableMobileButton,
  MeasurementStyledText,
  UnavailableButton,
  UnavailableMobileButton,
  WardrobeContainer,
  WardrobeItemContainer,
} from '@views/jobs/components/InvitedBox/styles';
import {
  InvitedBoxSubmittedUk,
  InvitedBoxSubmittedUs,
} from '@views/jobs/components/InvitedBox/Submitted';
import {
  InvitedBoxUnavailableUk,
  InvitedBoxUnavailableUs,
} from '@views/jobs/components/InvitedBox/Unavailable';
import {
  ArchiveAlert,
  CustomModalContent,
  JobButton,
  ModalContent,
  ModalHeader,
  ModalTitle,
  StyledAvatarContent,
  StyledForm,
  StyledTitleAvatarContainer,
  SubmitStyledtext,
  SubmitToJobName,
} from '@views/jobs/components/statusBoxStyles';
import { WardrobeQuestionUK } from '@views/jobs/components/WardrobeQuestionUK';

import Warning from '../../../../svg/Warning.svg';
import { ApplicationQuestion } from '../../../../views/jobs/components/ApplicationQuestion';
import { CustomQuestion } from '../../../../views/jobs/components/CustomQuestion';
import { WardrobeQuestion } from '../../../../views/jobs/components/WardrobeQuestion';

type Props = {
  props: SubmissionDetails | SubmissionDetailsMisc;
  onFlagChange: any;
};

const BoxData = ({ props, onFlagChange }: Props): JSX.Element => {
  const isUkProject = props?.project_location_country === UK_COUNTRY;
  const userId = useSelector((state: RootState) => state.app?.user.profile?.id);
  const gender = useSelector(
    (state: RootState) => state.app?.user.IUserInfoDAO?.about?.gender
  );
  const userProfile = useSelector((state: RootState) => state.app.user.profile);
  const InitialtUserAppearance = useSelector(
    (state: RootState) => state.app.user?.UserAppearance
  );

  const applicationQuestionValuesByColumnId =
    props?.application_question_list_column_values_by_column_id;

  const application = props?.applications[0];

  const applicationQuestions = application?.application_questions;
  const initialFormData =
    applicationQuestions?.reduce((acc, question) => {
      const questionId = question?.list_column_id;
      const questionValue =
        applicationQuestionValuesByColumnId?.[questionId]?.values?.value;
      if (questionValue) {
        acc[questionId] = {
          list_column_id: questionId,
          response: { value: questionValue },
        };
      }
      return acc;
    }, {}) || {};

  const initialUploadedImages =
    applicationQuestions?.reduce((acc, question) => {
      if (question?.type === 'attachment') {
        const questionId = question?.list_column_id;
        const questionValue =
          applicationQuestionValuesByColumnId?.[questionId]?.values?.value;
        if (Array.isArray(questionValue)) {
          questionValue.forEach((imgSrc) => {
            acc.push({ columnId: questionId, imgSrc });
          });
        }
      }
      return acc;
    }, [] as { imgSrc: string; columnId: number }[]) || [];

  const {
    columnIdLoading,
    formInstance,
    uploadedImages,
    applicationResponses,
    customResponses,
    handleInputChange,
    handleFileChange,
    handleRemoveImage,
  } = useJobsForm(false, initialFormData, initialUploadedImages);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalVisibleMisc, setModalVisibleMisc] = useState(false);

  const [modalVisibleAvailable, setmodalVisibleAvailable] = useState(false);
  const [modalMobileVisibleAvailable, setmodalMobileVisibleAvailable] =
    useState(false);
  const [userAppearance, setUserAppearance] = useState(InitialtUserAppearance);

  const isMobileDevice = window.innerWidth <= 768;
  const profilePhotoUrl = userProfile?.profile_photo.url;

  const [wardrobeForm, setWardrobeForm] = useState({
    dress: userAppearance?.dress || null,
    chest_bust: userAppearance?.chest_bust || null,
    neck: userAppearance?.neck || null,
    sleeve: userAppearance?.sleeve || null,
    hip: userAppearance?.hip || null,
    waist: userAppearance?.waist || null,
    inseam: userAppearance?.inseam || null,
    pants: userAppearance?.pants || null,
    bust: userAppearance?.bust || null,
    cup: userAppearance?.cup || null,
    shirt_size: userAppearance?.shirt_size || null,
    shoe_size: userAppearance?.shoe_size || null,
    hat: userAppearance?.hat || null,
    uk_dress: userAppearance?.uk_dress || null,
    chest: userAppearance?.chest || null,
    underbust: userAppearance?.underbust || null,
    natural_waist: userAppearance?.natural_waist || null,
    trouser_waist: userAppearance?.trouser_waist || null,
    uk_shoe: userAppearance?.uk_shoe || null,
    us_coat_length: userAppearance?.us_coat_length || null,
  });

  const wardrobeGender =
    gender === 'Prefer Not To Say'
      ? 'Other'
      : gender === 'Transgender Male'
      ? 'Male'
      : gender === 'Transgender Female'
      ? 'Female'
      : gender; // Male, Female, Other, Non-Binary

  const wardrobeLabelsByGender = isUkProject
    ? WARDROBE_LABELS_BY_GENDER_UK
    : WARDROBE_LABELS_BY_GENDER;
  const attributeSet = wardrobeLabelsByGender?.[wardrobeGender];

  const customQuestions = props?.custom_questions;
  const wardrobeQuestions = application?.required_wardrobe;
  const genderRelatedAttributes = wardrobeQuestions?.filter((attribute) => {
    return attributeSet?.includes(attribute);
  });

  const allAttributes = useMemo(
    () => genderRelatedAttributes || [],
    [genderRelatedAttributes]
  );

  const wardrobeMapping = isUkProject ? WardrobeMappingUK : WardrobeMapping;
  const mappedWardrobe = allAttributes?.map(
    (attribute) => wardrobeMapping[attribute] // Check UK
  );

  const handleWardrobeChange = (name, value) => {
    const shouldTrimValue = typeof value === 'string';
    const newValue = shouldTrimValue
      ? value.trim() === ''
        ? null
        : value
      : value;
    setWardrobeForm((prevFormData) => ({
      ...prevFormData,
      [name]: newValue,
    }));
  };
  const formattedDate = props?.bg_last_updated;

  const router = useRouter();
  const { list_membership_id, notification_id } = router.query;

  const handleAvailable = () => {
    if (isMobileDevice) {
      const appQuestionValuesByColumnId = Object.fromEntries(
        Object.entries(applicationQuestionValuesByColumnId || {}).map(
          ([columnId, value]) => [columnId, value?.values?.value]
        )
      );
      const queryString = stringify({
        list_membership_id: list_membership_id,
        JobName: props?.project_name,
        Role: props?.related_column_values?.Role,
        questionData: JSON?.stringify(applicationQuestions),
        Notification_id: props?.notification_id,
        custom_questions: JSON?.stringify(customQuestions),
        wardrobeQuestions: JSON?.stringify(wardrobeQuestions),
        appQuestionValuesByColumnId: JSON.stringify(
          appQuestionValuesByColumnId
        ),
      });
      router.push({
        pathname: '/jobs/AvailChecks/Invited/SubmitToJobMobile',
        search: queryString,
      });
    } else {
      setModalVisible(true);
    }
  };
  const handleMisc = useCallback(() => {
    let project_name = '';
    if (props.project_name) {
      project_name = props?.project_name;
    }
    if (isMobileDevice) {
      const queryString = stringify({
        project_name: project_name,
        list_membership_id: list_membership_id,
        userId: userId,
        Notification_id: props?.notification_id,
        custom_questions: customQuestions,
        Role: props?.related_column_values?.Role,
      });

      router.push({
        pathname: '/jobs/AvailChecks/Invited/CustomQuestionsInvitedMobile',
        search: queryString,
      });
    } else {
      setModalVisibleMisc(true);
    }
  }, [
    props?.project_name,
    isMobileDevice,
    list_membership_id,
    userId,
    props?.notification_id,
    customQuestions,
    props?.related_column_values?.Role,
    router,
    setModalVisibleMisc,
  ]);

  const handleModalClose = () => {
    setModalVisible(false);
    setModalVisibleMisc(false);
    setmodalVisibleAvailable(false);
  };
  const handleSubmitToJob = () => {
    handleSubmitJob();
    setModalVisible(false);
  };

  const handleSubmit = () => {
    handleStatus('unavailable');
    if (isMobileDevice) {
      setmodalMobileVisibleAvailable(false);
    } else {
      setmodalVisibleAvailable(false);
    }
  };
  const handleNoLongerAvailable = () => {
    if (isMobileDevice) {
      setmodalMobileVisibleAvailable(true);
    } else {
      setmodalVisibleAvailable(true);
    }
  };

  const list_membership_idNumber = parseInt(
    typeof list_membership_id === 'string' && list_membership_id,
    10
  );
  const notification_idNumber =
    parseInt(typeof notification_id === 'string' && notification_id, 10) ||
    props?.notification_id;

  const handleStatus = (status) => {
    listMemberships
      .updateUserSubmission(
        typeof list_membership_id === 'string' && list_membership_id,
        { list_status: status }
      )
      .then(() => {
        message.success('Job status Successfully Saved');
        onFlagChange(true);
      })
      .catch(() => {
        message.error('Error');
      });
  };

  const APIData = props?.in_app_messages[0]?.message;
  const EditableSnippet = props?.in_app_messages[0]?.editable_snippet;
  const MiscAPIData = props?.notification_message;

  useEffect(() => {
    const getAppearance = async () => {
      const UserAppearance = await UsersQueryApi.getUserAppearance(userId);
      setUserAppearance(UserAppearance);
    };
    getAppearance();
  }, [userId]);

  useEffect(() => {
    const submitBtn = document.getElementById('submit-to-job-btn');
    const UnavailableBtn = document.getElementById('unavailable-btn');

    !props.is_archived &&
      submitBtn &&
      submitBtn?.addEventListener('click', function () {
        handleAvailable();
      });

    !props.is_archived &&
      UnavailableBtn &&
      UnavailableBtn?.addEventListener('click', function () {
        handleStatus('unavailable');
      });

    return () => {
      submitBtn && submitBtn?.removeEventListener('click', handleAvailable);
      UnavailableBtn &&
        UnavailableBtn?.removeEventListener('click', handleStatus);
    };
  }, [APIData, props?.list_status]);

  useEffect(() => {
    const MiscRespondBtn = document.getElementById('message-response-btn');

    MiscRespondBtn &&
      MiscRespondBtn?.addEventListener('click', function () {
        handleMisc();
      });

    return () => {
      MiscRespondBtn &&
        MiscRespondBtn?.removeEventListener('click', handleMisc);
    };
  }, [MiscAPIData, props?.list_status, handleMisc]);

  const handleSubmitJob = () => {
    if (allAttributes?.length > 0)
      UsersCommandApi.updateUserAppearanceCall(userId, wardrobeForm);

    customQuestions?.length &&
      listMemberships.SubmitCustomQuestions({
        list_membership_id: list_membership_idNumber,
        user_id: userId,
        notification_id: notification_idNumber,
        responses: customResponses,
      });

    listMemberships
      .SubmitApplication({
        list_membership_id: list_membership_idNumber,
        user_id: userId,
        ...(applicationResponses?.length && {
          application_responses: applicationResponses,
        }),
      })
      .then(() => {
        message.success('Job status Successfully Saved');
        onFlagChange(true);
        router.push(window.location);
      })
      .catch((error) => {
        message.error('Error: ' + error?.toString());
      });
  };

  const handleSubmitToResponse = () => {
    customResponses?.length &&
      listMemberships
        .SubmitCustomQuestions({
          list_membership_id: list_membership_idNumber,
          user_id: userId,
          notification_id: notification_idNumber,
          responses: customResponses,
        })
        .then(() => {
          message.success('Submitted Successfully');
          notificationIdYesOrNo && onFlagChange(true);
        })
        .catch((error) => {
          message.error('Error: ' + error?.toString());
          console.log('error');
        });

    handleModalClose();
  };
  const requiredCount = customQuestions?.reduce(
    (count, question) => (question?.current_value !== null ? count + 1 : count),
    0
  );

  const currentUrl = router?.asPath;
  const notificationIdYesOrNo = currentUrl?.includes('notification_id');

  return (
    <>
      {props.is_archived && !MiscAPIData && (
        <ArchiveAlert
          icon={<img src={Warning.src} alt='Warning Icon' />}
          message='This project has been archived. No actions can be taken at this time.'
          showIcon
        />
      )}
      {!MiscAPIData &&
        props?.list_status === 'submitted' &&
        (isUkProject ? (
          <InvitedBoxSubmittedUk
            isMobileDevice={isMobileDevice}
            handleNoLongerAvailable={handleNoLongerAvailable}
            bg_last_updated={props?.bg_last_updated}
            EditableSnippet={EditableSnippet}
            is_archived={props.is_archived}
          />
        ) : (
          <InvitedBoxSubmittedUs
            props={props}
            isMobileDevice={isMobileDevice}
            formattedDate={formattedDate}
            handleNoLongerAvailable={handleNoLongerAvailable}
            EditableSnippet={EditableSnippet}
          />
        ))}
      {!MiscAPIData && props?.list_status === 'invited' && (
        <div
          className='static-html-view-invites-invited'
          style={{ ...(isMobileDevice ? { padding: 16 } : {}) }}
          dangerouslySetInnerHTML={{ __html: APIData }}
        />
      )}
      {!MiscAPIData &&
        props?.list_status === 'unavailable' &&
        (isUkProject ? (
          <InvitedBoxUnavailableUk
            isMobileDevice={isMobileDevice}
            bg_last_updated={props?.bg_last_updated}
            handleAvailable={handleAvailable}
            is_archived={props?.is_archived}
          />
        ) : (
          <InvitedBoxUnavailableUs
            props={props}
            isMobileDevice={isMobileDevice}
            formattedDate={formattedDate}
            handleAvailable={handleAvailable}
          />
        ))}
      {MiscAPIData && (
        <div
          className='static-html-view-invites'
          style={{ ...(isMobileDevice ? { padding: 16 } : {}) }}
          dangerouslySetInnerHTML={{ __html: MiscAPIData }}
        />
      )}

      {!MiscAPIData && props?.list_status === 'released' && (
        <div
          className='static-html-view-avail-check-released'
          style={{ ...(isMobileDevice ? { padding: 16 } : {}) }}
          dangerouslySetInnerHTML={{ __html: APIData }}
        />
      )}

      <CustomModalContent
        open={modalVisible}
        onCancel={handleModalClose}
        footer={[
          <JobButton
            style={{ marginBottom: '16px' }}
            key='cancel'
            onClick={handleModalClose}
          >
            Cancel
          </JobButton>,
          <JobButton
            style={{ marginRight: '24px', marginBottom: '16px' }}
            key='submit'
            type='primary'
            onClick={formInstance.submit}
          >
            Submit To Job
          </JobButton>,
        ]}
      >
        <ModalHeader
          style={{
            paddingTop: '16px',
            paddingLeft: '24px',
          }}
        >
          Submit To Job
        </ModalHeader>
        <Divider
          style={{
            margin: '0px',
            marginBottom: '16px',
            marginTop: '16px',
            border: '1px solid #D8D8D8',
            borderWidth: '1px 0 0 0',
          }}
        />
        <ModalContent>
          <StyledTitleAvatarContainer
            style={{ paddingLeft: '24px', paddingRight: '24px' }}
          >
            <ModalTitle>
              {application?.job_name
                ? application?.job_name
                : props?.project_name}
            </ModalTitle>
            <StyledAvatarContent>
              <SubmitToJobName>
                {userProfile?.first_name} {userProfile?.last_name}{' '}
              </SubmitToJobName>
              {profilePhotoUrl ? (
                <Avatar
                  round={true}
                  color={theme.palette.primary[0]}
                  size='24'
                  src={profilePhotoUrl}
                  style={{
                    height: '24px',
                    width: '24px',
                    cursor: 'pointer',
                    marginLeft: '8px',
                  }}
                />
              ) : (
                <Avatar
                  round={true}
                  color={theme.palette.primary[0]}
                  size='24'
                  name={userProfile?.first_name
                    ?.concat('  ')
                    ?.concat(userProfile?.last_name)}
                  style={{
                    cursor: 'pointer',
                    marginLeft: '8px',
                    height: '24px',
                    width: '24px',
                  }}
                />
              )}
            </StyledAvatarContent>
          </StyledTitleAvatarContainer>

          <StyledForm
            form={formInstance}
            validateMessages={{ required: 'This field is required' }}
            onFinish={handleSubmitToJob}
            style={{ paddingLeft: '24px', paddingRight: '24px' }}
          >
            {applicationQuestions
              ?.filter(({ type }: { type: string }) => type !== 'attachment')
              ?.map((question, i) => (
                <div key={i}>
                  <ApplicationQuestion
                    form={formInstance}
                    loading={columnIdLoading[question.list_column_id]}
                    question={question}
                    uploadedImages={uploadedImages}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    handleRemoveImage={handleRemoveImage}
                    initialValue={
                      applicationQuestionValuesByColumnId?.[
                        question?.list_column_id
                      ]?.values?.value
                    }
                  />
                </div>
              ))}
            {customQuestions
              ?.filter(({ type }: { type: string }) => type !== 'attachment')
              ?.map((question, i) => (
                <div key={i}>
                  <CustomQuestion
                    form={formInstance}
                    loading={columnIdLoading[question.list_column_id]}
                    columnId={question.list_column_id}
                    question={question}
                    uploadedImages={uploadedImages}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    handleRemoveImage={handleRemoveImage}
                  />
                </div>
              ))}
            {mappedWardrobe && mappedWardrobe?.length > 0 && (
              <>
                <MeasurementStyledText>Measurements</MeasurementStyledText>
                <WardrobeContainer>
                  {mappedWardrobe?.map((item, i) => (
                    <WardrobeItemContainer key={`${item}${i}`} i={i}>
                      {isUkProject ? (
                        <WardrobeQuestionUK
                          label={item}
                          userAppearance={userAppearance}
                          handleWardrobeChange={handleWardrobeChange}
                          isMinor={userProfile.is_minor}
                        />
                      ) : (
                        <WardrobeQuestion
                          label={item}
                          userAppearance={userAppearance}
                          handleWardrobeChange={handleWardrobeChange}
                          isMinor={userProfile.is_minor}
                        />
                      )}
                    </WardrobeItemContainer>
                  ))}
                </WardrobeContainer>
              </>
            )}
            {applicationQuestions
              ?.filter(({ type }: { type: string }) => type === 'attachment')
              ?.map((question, i) => (
                <div key={i}>
                  <ApplicationQuestion
                    form={formInstance}
                    loading={columnIdLoading[question.list_column_id]}
                    question={question}
                    uploadedImages={uploadedImages}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    handleRemoveImage={handleRemoveImage}
                    initialValue={
                      applicationQuestionValuesByColumnId?.[
                        question?.list_column_id
                      ]?.values?.value
                    }
                  />
                </div>
              ))}
            {customQuestions
              ?.filter(({ type }: { type: string }) => type === 'attachment')
              ?.map((question, i) => (
                <div key={i}>
                  <CustomQuestion
                    form={formInstance}
                    loading={columnIdLoading[question.list_column_id]}
                    columnId={question.list_column_id}
                    question={question}
                    uploadedImages={uploadedImages}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    handleRemoveImage={handleRemoveImage}
                  />
                </div>
              ))}

            {(!customQuestions?.length && !application) ||
              (!applicationQuestions && (
                <SubmitStyledtext>
                  Confirm your submission to this role?
                </SubmitStyledtext>
              ))}
          </StyledForm>
        </ModalContent>
        <Divider
          style={{
            marginBottom: '16px',
            marginTop: '16px',
            border: '1px solid #D8D8D8',
            borderWidth: '1px 0 0 0',
          }}
        />
      </CustomModalContent>

      {/* Misc Message Modal */}
      {modalVisibleMisc && (
        <CustomModalContent
          open={modalVisibleMisc}
          onCancel={handleModalClose}
          footer={[
            requiredCount !== customQuestions?.length && (
              <JobButton
                style={{ marginBottom: '16px' }}
                key='cancel'
                onClick={handleModalClose}
              >
                Cancel
              </JobButton>
            ),
            requiredCount !== customQuestions?.length ? (
              <JobButton
                style={{ marginRight: '24px', marginBottom: '16px' }}
                key='submit'
                type='primary'
                onClick={formInstance.submit}
              >
                Submit Response
              </JobButton>
            ) : (
              <JobButton
                style={{ marginRight: '24px', marginBottom: '16px' }}
                key='cancel'
                onClick={handleModalClose}
              >
                Close
              </JobButton>
            ),
          ]}
          wrapClassName='custom-modal'
        >
          {requiredCount !== customQuestions?.length ? (
            <ModalHeader
              style={{
                paddingTop: '16px',
                paddingLeft: '24px',
                paddingRight: '24px',
              }}
            >
              Responding to {props?.project_name} |{' '}
              {props?.related_column_values?.Role}
            </ModalHeader>
          ) : (
            <ModalHeader
              style={{
                paddingTop: '16px',
                paddingLeft: '24px',
                paddingRight: '24px',
              }}
            >
              Viewing Response to {props?.project_name} |{' '}
              {props?.related_column_values?.Role}
            </ModalHeader>
          )}
          <Divider
            style={{
              margin: '0px',
              marginBottom: '16px',
              marginTop: '16px',
              border: '1px solid #D8D8D8',
              borderWidth: '1px 0 0 0',
            }}
          />
          <ModalContent>
            <StyledForm
              form={formInstance}
              onFinish={handleSubmitToResponse}
              validateMessages={{ required: 'This field is required' }}
              style={{ paddingLeft: '24px', paddingRight: '24px' }}
            >
              {customQuestions?.map((question, i) => (
                <div key={`${question}${i}`}>
                  <CustomQuestion
                    form={formInstance}
                    loading={columnIdLoading[question.list_column_id]}
                    columnId={question.list_column_id}
                    question={question}
                    uploadedImages={uploadedImages}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    handleRemoveImage={handleRemoveImage}
                  />
                </div>
              ))}

              {!customQuestions?.length && (
                <SubmitStyledtext>
                  Confirm your submission to this role?
                </SubmitStyledtext>
              )}
            </StyledForm>
          </ModalContent>
          <Divider
            style={{
              border: '1px solid #D8D8D8',
              marginBottom: '16px',
              marginTop: '16px',
              borderWidth: '1px 0 0 0',
            }}
          />
        </CustomModalContent>
      )}

      <Modal
        open={modalVisibleAvailable}
        onCancel={handleModalClose}
        footer={[
          <AvailableButton key='cancel' onClick={handleModalClose}>
            Stay as Available
          </AvailableButton>,
          <UnavailableButton key='submit' type='primary' onClick={handleSubmit}>
            Update to Unavailable
          </UnavailableButton>,
        ]}
        wrapClassName='custom-modal'
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ModalHeader style={{ marginBottom: '16px' }}>
          Change Status to Unavailable?
        </ModalHeader>
        <ModalContent>
          <SubmitStyledtext>
            You previously submitted for this job. Do you wish to change your
            status to Unavailable?
          </SubmitStyledtext>
        </ModalContent>
      </Modal>

      <Modal
        open={modalMobileVisibleAvailable}
        onCancel={() => {
          setmodalMobileVisibleAvailable(false);
        }}
        wrapClassName='custom-modal'
        footer={null}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          top: '50%',
          transform: ' translateY(-50%)',
        }}
        closable={false}
      >
        <ModalHeader>Change Status to Unavailable?</ModalHeader>
        <Divider
          style={{ margin: '0px', marginBottom: '16px', marginTop: '16px' }}
        />
        <ModalContent
          style={{ width: '30px !important', height: '40px !important' }}
        >
          <SubmitStyledtext>
            You previously submitted for this job. Do you wish to change your
            status to Unavailable?
          </SubmitStyledtext>
          <br />
        </ModalContent>
        <ModalContent
          style={{
            width: '30px !important',
            height: '40px !important',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <hr style={{ width: '114%', color: 'black' }} />
          <br />
          <UnavailableMobileButton
            onClick={handleSubmit}
            style={{
              color: '#007AFF',
            }}
          >
            Update to Unavailable
          </UnavailableMobileButton>
          <br />
          <hr style={{ width: '114%', color: 'black' }} />
          <br />

          <AvailableMobileButton
            onClick={() => {
              setmodalMobileVisibleAvailable(false);
            }}
            style={{
              color: '#007AFF',
              fontWeight: '400',
            }}
          >
            Stay as Available
          </AvailableMobileButton>
        </ModalContent>
        <Divider
          style={{
            border: '1px solid #D8D8D8',
            marginBottom: '16px',
            marginTop: '16px',
            borderWidth: '1px 0 0 0',
          }}
        />
      </Modal>
    </>
  );
};
export default BoxData;
